import { isPlatformBrowser } from '@angular/common';
import { inject, Injectable, PLATFORM_ID } from '@angular/core';
export enum OS {
  MacOS = 'Mac OS',
  iOS = 'iOS',
  Windows = 'Windows',
  Linux = 'Linux',
  UNIX = 'UNIX',
  ChromeOS = 'Chrome OS',
  OS2 = 'OS/2',
  QNX = 'QNX',
  Android = 'Android',
  BlackBerry = 'BlackBerry',
  SearchBot = 'Search Bot',
}
export interface DeviceInfo {
  OS: string;
  browser: string;
  isMobile?: boolean;
}
const UNKNOWN = 'Unknown';
@Injectable({ providedIn: 'root' })
export class DeviceInfoService {
  private readonly platformId = inject(PLATFORM_ID);

  private readonly _device: DeviceInfo = { OS: UNKNOWN, browser: UNKNOWN };
  constructor() {
    if (isPlatformBrowser(this.platformId)) {
      this._device = this._setDeviceInfo(window.navigator);
    }
  }
  get DeviceInfo(): DeviceInfo {
    return this._device;
  }
  /**
   * Returns corresponding commandkey value based on OS
   * @returns string
   */
  public getCommandKeyByOS(): string {
    const matchesDeviceOS = os => os === this._device?.OS;
    return [OS.MacOS, OS.iOS].some(matchesDeviceOS) ? 'Command' : 'Ctrl';
  }
  private _setDeviceInfo(navigator: Navigator): DeviceInfo {
    return {
      OS: this._getOS(navigator) ?? UNKNOWN,
      // even when appName is deprecated yet there is no better way to obtain this value
      // unless userAgentData is fully supported by most browsers which as of 9-24-22 has
      // not happened
      browser: this._getBrowser(navigator) ?? UNKNOWN,
      isMobile: navigator['userAgentData']?.mobile ?? false,
    };
  }
  private _getOS(navigator: Navigator) {
    const userAgent = navigator.userAgent;
    let DEVICE_REGEX_MAPPING = [
      { system: OS.MacOS, rgx: /Mac OS X/ },
      { system: OS.MacOS, rgx: /(Mac OS|MacPPC|MacIntel|Mac_PowerPC|Macintosh)/ },
      { system: OS.iOS, rgx: /(iPhone|iPad|iPod)/ },
      { system: OS.Windows, rgx: /(Windows 10.0|Windows NT 10.0)/ },
      { system: OS.Windows, rgx: /(Windows 8.1|Windows NT 6.3)/ },
      { system: OS.Windows, rgx: /(Windows 8|Windows NT 6.2)/ },
      { system: OS.Windows, rgx: /(Windows 7|Windows NT 6.1)/ },
      { system: OS.Windows, rgx: /Windows NT 6.0/ },
      { system: OS.Windows, rgx: /Windows NT 5.2/ },
      { system: OS.Windows, rgx: /(Windows NT 5.1|Windows XP)/ },
      { system: OS.Windows, rgx: /(Windows NT 5.0|Windows 2000)/ },
      { system: OS.Windows, rgx: /(Win 9x 4.90|Windows ME)/ },
      { system: OS.Windows, rgx: /(Windows 98|Win98)/ },
      { system: OS.Windows, rgx: /(Windows 95|Win95|Windows_95)/ },
      { system: OS.Windows, rgx: /(Windows NT 4.0|WinNT4.0|WinNT|Windows NT)/ },
      { system: OS.Windows, rgx: /Windows CE/ },
      { system: OS.Windows, rgx: /Win16/ },
      { system: OS.ChromeOS, rgx: /CrOS/ },
      { system: OS.Android, rgx: /Android/ },
      { system: OS.Linux, rgx: /(Linux|X11(?!.*CrOS))/ },
      { system: OS.UNIX, rgx: /OpenBSD/ },
      { system: OS.UNIX, rgx: /SunOS/ },
      { system: OS.QNX, rgx: /QNX/ },
      { system: OS.UNIX, rgx: /UNIX/ },
      { system: OS.BlackBerry, rgx: /BlackBerry/ },
      { system: OS.OS2, rgx: /OS\/2/ },
      { system: OS.SearchBot, rgx: /(nuhk|Googlebot|Yammybot|Openbot|Slurp|MSNBot|Ask Jeeves\/Teoma|ia_archiver)/ },
    ];
    return DEVICE_REGEX_MAPPING.find(systemRegex => systemRegex.rgx.test(userAgent))?.system;
  }
  private _getBrowser(navigator: Navigator) {
    const ngAgent = navigator.appName;
    let browser = '';
    if (['Opera', 'OPR'].some(opera => ngAgent.indexOf(opera) != -1)) {
      // Opera
      browser = 'Opera';
    } else if (['Edge', 'Edg'].some(edge => ngAgent.indexOf(edge) != -1)) {
      // Legacy Edge || Edge (Chromium)
      browser = 'Microsoft Edge';
    } else if (['MSIE', 'Trident/'].some(msie => ngAgent.indexOf(msie) != -1)) {
      // MSIE
      browser = 'Microsoft Internet Explorer';
    } else if (ngAgent.indexOf('Chrome') != -1) {
      // Chrome
      browser = 'Chrome';
    } else if (ngAgent.indexOf('Safari') != -1) {
      // Safari
      browser = 'Safari';
    } else if (ngAgent.indexOf('Firefox') != -1) {
      // Firefox
      browser = 'Firefox';
    } else {
      // Other browsers
      browser = window.navigator.appName;
    }
    return browser;
  }
}
